<template>
  <div class="product-row">
    <div v-if="model.AnchorId" :id="model.AnchorId" />
    <RowHeader
      :title="title"
      :lead="description"
      :href="model.Link || model.ViewMoreDestination"
      :linkText="model.LinkText || model.ViewMoreLink"
      :fontColor="`${contrastingFontColorTop}`"
      @link-click="$emit('link-click', $event)"
    />
    <div class="product-row-products">
      <GenericSlider
        v-if="model.Products !== null && model.Products.length > 0"
        :visibleSlides="visibleSlides"
        :controls="$mqAliases.desktop"
        :fullWidth="true"
      >
        <div
          v-for="(item, index) in productsComputedList"
          :ref="`slide${index}`"
          :key="`slot-${item.Code || index}-${index}`"
          class="generic-slider-item -p-relative"
        >
          <div
            v-if="item.label"
            class="font-responsive-small -small-caps m-b-6 m-t-6"
          >
            {{ item.label }}
          </div>
          <div v-if="item !== 'placeholder' && item.Code !== 'placeholder'">
            <ProductCard
              :key="`item-${item.Code}-${index}`"
              :model="item"
              :listName="listName"
              :wishlistCard="wishlistCard"
              :startVariantColor="
                item.startVariantColor ? item.startVariantColor : ''
              "
              :presetSize="item.selectedSize ? item.selectedSize : ''"
              :index="index"
              :recoVersion="recoVersion"
              :outlet="outlet"
              :imageBackgroundColor="
                model.TransparentBackgroundImages
                  ? 'transparent'
                  : BGCOLOR.LIGHTER_GRAY
              "
              :transparent="!!sliderBackgroundColor || blackTheme"
              :fontColorOverride="contrastingFontColorBottom"
              :themeColor="
                sliderBackgroundColor || (blackTheme ? '#1d1d1d' : '')
              "
              :deactivateMobileSwipe="true"
              :trackSourceObject="category || null"
              :showQuickBuy="!disableQuickBuy"
              :cols="columns"
              :wishlistItems="wishlistItems"
              :featureLookImages="enableLookImages"
              :firstImageType="setCloseupAsFirstImage ? 'look-c' : ''"
            />
          </div>

          <div
            v-else
            :key="`empty-${index}`"
            :style="{
              backgroundColor: emptyColor || bgColor,
              height: `calc(100% - ${item.label ? 123 : 95}px);`,
            }"
          />
        </div>
      </GenericSlider>
    </div>
  </div>
</template>
<script>
import RowHeader from '@/Scripts/components/blocks/ContentSliderAssets/RowHeader.vue';
import GenericSlider from '@/Scripts/components/shared/GenericSlider/GenericSlider.vue';
import { SplitAllColors, RemoveEmptyProducts } from '@/Scripts/lib/helper/productListHelper.js';
import siteInfo from '@/Scripts/lib/helper/siteInfo.js';

import { BGCOLOR } from '@/Scripts/constants';
import { mapState } from 'vuex';
import TinyColor from 'tinycolor2';
import { TrackerProductObject, TrackerImpressions } from '@/Scripts/lib/helper/trackingHelper.js';

import ProductCard from '@/Scripts/components/productcards/ProductCard/ProductCard.vue';

export default {
  name: 'ProductRowBlock',
  mixins: [siteInfo],
  components: {
    GenericSlider,
    RowHeader,
    ProductCard
  },
  props: {
    /**
     * Block object
     */
    model: {
      type: Object,
      required: true
    },
    rowid: {
      type: String,
      default: '0'
    },
    outlet: {
      type: Boolean,
      default: false
    },
    disableScroll: {
      type: Boolean,
      default: false
    },
    showScrollbar: {
      type: Boolean,
      default: false
    },
    selectedColors: {
      type: Array,
      required: false,
      default: () => []
    },
    selectedSizes: {
      type: Array,
      required: false,
      default: () => []
    },
    pageType: String,
    category: String,
    recoVersion: String,
    showQuickBuy: {
      type: Boolean,
      default: true
    },
    overrideVisibleSlides: Object,
    wishlistItems: {
      type: Array,
      default: () => []
    },
    allowEmpty: Boolean,
    wishlistCard: Boolean,
    listName: String

  },
  data () {
    return {
      scrollHandlePos: 0,
      isInReco: false,
      BGCOLOR: BGCOLOR,
      emptyColor: '#F5F5F5',
      trackedItems: [],
      enableLookImages: false,
      setCloseupAsFirstImage: false
    };
  },
  watch: {
    productsComputedList: {
      immediate: true,
      handler (newvalue) {
        this.trackImpressions(newvalue);
      }
    }
  },
  computed: {
    ...mapState({
      blackTheme: state => state.appContext.blackTheme,
      featureLookImages: state => state.appContext.featureLookImages
    }),
    columns () {
      return [{ cols: 1.5, breakPoint: 0 }, { cols: 2.6, breakPoint: 'MD' }, { cols: 3, breakPoint: 'LG' }];
    },
    disableQuickBuy () {
      return !this.showQuickBuy || (this.$mqAliases.desktop && this.model.ListStyle === 1);
    },
    visibleSlides () {
      if (this.overrideVisibleSlides) {
        return this.$mqAliases.desktop ? this.overrideVisibleSlides.lg : this.$mqAliases.md ? this.overrideVisibleSlides.md : this.overrideVisibleSlides.sm;
      }
      return this.$mqAliases.desktop ? this.desktopSlides : this.$mqAliases.md ? this.tabletSlides : this.mobileSlides;
    },
    desktopSlides () {
      return this.model.ListStyle === 0 ? 4 : 5;
    },
    mobileSlides () {
      return this.model.MobileListStyle === 0 ? 1.5 : 2.5;
    },
    tabletSlides () {
      return this.model.ListStyle === 0 ? 2.6 : 3.6;
    },
    bgColor () {
      return !this.blackTheme ? BGCOLOR.DEFAULT : BGCOLOR.BLACK;
    },
    productsComputedList () {
      const list = !this.allowEmpty ? RemoveEmptyProducts(this.model.Products, this.outlet) : this.model.Products;
      if (this.selectedColors.length > 0) {
        return SplitAllColors(list, this.selectedColors, this.outlet);
      }
      return list;
    },
    hasDestination () {
      return (typeof this.model.ViewMoreDestination !== 'undefined' && this.model.ViewMoreDestination !== '');
    },
    hasViewMoreLink () {
      return (
        typeof this.model.ViewMoreLink !== 'undefined' &&
        this.model.ViewMoreLink !== ''
      );
    },
    sliderBackgroundColor () {
      return this.model.ColorOptions.GradientTo || this.model.ColorOptions.BackgroundColor || '';
    },
    contrastingFontColorTop () {
      if (this.model.ColorOptions && this.model.ColorOptions.FontColor) {
        return this.model.ColorOptions.FontColor;
      }
      return (this.model.ColorOptions.BackgroundColor && TinyColor.mostReadable(this.model.ColorOptions.BackgroundColor, ['#FCFCFC', '#1D1D1D']).toHexString()) || this.blackTheme ? '#FCFCFC' : '' || '';
    },
    contrastingFontColorBottom () {
      if (this.model.ColorOptions && this.model.ColorOptions.FontColor) {
        return this.model.ColorOptions.FontColor;
      }
      return (this.sliderBackgroundColor && TinyColor.mostReadable(this.sliderBackgroundColor, ['#FCFCFC', '#1D1D1D']).toHexString()) || this.blackTheme ? '#FCFCFC' : '' || '';
    },
    title () {
      return typeof this.model.Title !== 'undefined' &&
        this.model.Title !== null
        ? this.model.Title.replace(/<[^>]*>/g, '')
        : '';
    },
    description () {
      return typeof this.model.Description !== 'undefined' &&
        this.model.Description !== null
        ? this.model.Description
        : '';
    }
  },
  methods: {
    trackImpressions (products) {
      const itemsToTrack = [];
      // find products and try to get it's displayed variant. Add it to track list if it's not tracked before
      products.forEach((product, index) => {
        if (product !== 'placeholder' && 'Code' in product && !this.trackedItems[index]) {
          const mainColor = !this.outlet ? product.MainColor : product.OutletMainColor;
          const variants = [...(product.ActiveVariants || []), ...(product.OutletVariants || []), ...(product.ArchivedVariants || [])];
          const variant = variants.find(v => v.ColorCode === mainColor) || variants[0];
          const obj = TrackerProductObject({
            product: product,
            colorVariant: variant,
            outlet: this.outlet
          });
          obj.index = index;
          itemsToTrack.push(obj);
          this.trackedItems[index] = true;
        }
      });

      if (itemsToTrack && itemsToTrack.length) {
        TrackerImpressions(itemsToTrack, this.title);
      }
    }
  },
  created () {
    if (this.pageType === 'ActivityCollection') {
      this.enableLookImages = this.featureLookImages;
      // this.setCloseupAsFirstImage = true;
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.$emit('ready');
    });
  }
};
</script>

<style lang="scss">
@import "@/scss/defaults.scss";
.product-row {
  padding-bottom: 36px;
  @include media-breakpoint-up(md) {
    padding-bottom: 60px;
  }
  overflow-x: hidden;
  .header {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
  &-header {
    //margin-bottom: 9px;
    @include media-breakpoint-up(md) {
      margin-bottom: 18px;
    }
    cursor: pointer;
  }
}
</style>
